/**
 * 2026 Tax Year Configuration
 *
 * Federal tax brackets, capital gains rates, and standard deductions.
 * Source: IRS Rev. Proc. 2025-32 (as amended by the One Big Beautiful Bill Act);
 * Social Security wage base from SSA 2026 COLA fact sheet.
 */

import { FederalTaxConfig } from '../../types.js';

export const federal2026: FederalTaxConfig = {
  brackets: {
    single: [
      { min: 0, max: 12400, rate: 0.10 },
      { min: 12400, max: 50400, rate: 0.12 },
      { min: 50400, max: 105700, rate: 0.22 },
      { min: 105700, max: 201775, rate: 0.24 },
      { min: 201775, max: 256225, rate: 0.32 },
      { min: 256225, max: 640600, rate: 0.35 },
      { min: 640600, max: Infinity, rate: 0.37 }
    ],
    marriedFilingJointly: [
      { min: 0, max: 24800, rate: 0.10 },
      { min: 24800, max: 100800, rate: 0.12 },
      { min: 100800, max: 211400, rate: 0.22 },
      { min: 211400, max: 403550, rate: 0.24 },
      { min: 403550, max: 512450, rate: 0.32 },
      { min: 512450, max: 768700, rate: 0.35 },
      { min: 768700, max: Infinity, rate: 0.37 }
    ],
    marriedFilingSeparately: [
      { min: 0, max: 12400, rate: 0.10 },
      { min: 12400, max: 50400, rate: 0.12 },
      { min: 50400, max: 105700, rate: 0.22 },
      { min: 105700, max: 201775, rate: 0.24 },
      { min: 201775, max: 256225, rate: 0.32 },
      { min: 256225, max: 384350, rate: 0.35 },
      { min: 384350, max: Infinity, rate: 0.37 }
    ],
    headOfHousehold: [
      { min: 0, max: 17700, rate: 0.10 },
      { min: 17700, max: 67450, rate: 0.12 },
      { min: 67450, max: 105700, rate: 0.22 },
      { min: 105700, max: 201775, rate: 0.24 },
      { min: 201775, max: 256200, rate: 0.32 },
      { min: 256200, max: 640600, rate: 0.35 },
      { min: 640600, max: Infinity, rate: 0.37 }
    ]
  },

  capitalGainsBrackets: {
    single: [
      { min: 0, max: 49450, rate: 0.00 },
      { min: 49450, max: 545500, rate: 0.15 },
      { min: 545500, max: Infinity, rate: 0.20 }
    ],
    marriedFilingJointly: [
      { min: 0, max: 98900, rate: 0.00 },
      { min: 98900, max: 613700, rate: 0.15 },
      { min: 613700, max: Infinity, rate: 0.20 }
    ],
    marriedFilingSeparately: [
      { min: 0, max: 49450, rate: 0.00 },
      { min: 49450, max: 306850, rate: 0.15 },
      { min: 306850, max: Infinity, rate: 0.20 }
    ],
    headOfHousehold: [
      { min: 0, max: 66200, rate: 0.00 },
      { min: 66200, max: 579600, rate: 0.15 },
      { min: 579600, max: Infinity, rate: 0.20 }
    ]
  },

  standardDeductions: {
    single: 16100,
    marriedFilingJointly: 32200,
    marriedFilingSeparately: 16100,
    headOfHousehold: 24150
  },

  niitThresholds: {
    single: 200000,
    marriedFilingJointly: 250000,
    marriedFilingSeparately: 125000,
    headOfHousehold: 200000
  },

  niitRate: 0.038,

  socialSecurityWageBase: 184500,
  socialSecurityRate: 0.062,

  medicareRate: 0.0145,
  additionalMedicareRate: 0.009,
  additionalMedicareThresholds: {
    single: 200000,
    marriedFilingJointly: 250000,
    marriedFilingSeparately: 125000,
    headOfHousehold: 200000
  },

  // AMT (Alternative Minimum Tax) - IRS Rev. Proc. 2025-32
  amtExemptions: {
    single: 90100,
    marriedFilingJointly: 140200,
    marriedFilingSeparately: 70100,
    headOfHousehold: 90100
  },
  // OBBBA §70107 reset the phase-out start to $500k/$1M (no longer inflation-indexed)
  amtPhaseOutThresholds: {
    single: 500000,
    marriedFilingJointly: 1000000,
    marriedFilingSeparately: 500000,
    headOfHousehold: 500000
  },
  // OBBBA §70107 doubled the exemption phase-out rate from 25% to 50% starting 2026
  amtPhaseOutRate: 0.50,
  // 26%/28% rate breakpoint (MFS is half of the general threshold)
  amtHighRateThreshold: {
    single: 244500,
    marriedFilingJointly: 244500,
    marriedFilingSeparately: 122250,
    headOfHousehold: 244500
  },
  amtRates: [0.26, 0.28],
  // Itemized deductions — Rev. Proc. 2025-32 / OBBBA: $40,400 SALT cap,
  // reduced by 30% of MAGI over $505,000 but not below $10,000 (MFS: half of
  // each); 0.5%-of-AGI floor on itemized charity; $1,000/$2,000 charitable
  // deduction for non-itemizers; 2/37 itemized-benefit limitation (§68).
  itemizedDeductions: {
    saltCap: 40400,
    saltPhaseDownThreshold: 505000,
    saltPhaseDownRate: 0.30,
    saltFloor: 10000,
    charitableAgiFloorRate: 0.005,
    nonItemizerCharitableCap: { single: 1000, marriedFilingJointly: 2000 },
    itemizedBenefitLimitRate: 2 / 37
  },

  // IRS Notice 2025-67
  retirement401kLimit: 24500,
  retirement401kCatchup: 8000
};
